import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, Form, Input, Radio, Button, message, Checkbox } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import InputRut from '../../../../components/form/inputs/InputRut';
import { rutValidator } from '../../../../utils/validators';
import { validateRut } from '../../../../utils/rut';
import NotificationModal from '../../../../components/common/NotificationModal';
import { EnrollmentContext } from '../../../../providers/EnrollmentContextProvider';
import enrollmentAction from '../../../../providers/EnrollmentContextProvider/enrollmentAction';
import InitPage from '../../api-initPage';
import './styles.less';
import { EnrollmentFormContext } from '../../../../providers/EnrollmentFormContextProvider';
import EnrollmentView from '../../../../enums/EnrollmentView';

const InitialLegalForm = () => {
  const history = useHistory();
  const [rutIsAlreadyRegistered, setRutIsAlreadyRegistered] = useState();
  const [rutHasEnrollmentFormInProgress, setRutHasEnrollmentFormInProgress] = useState();
  const { dispatch } = useContext(EnrollmentContext);
  const { enrollmentForm } = useContext(EnrollmentFormContext);
  const [checkedTerms, setCheckedTerms] = useState(false);

  const allowLettersAndWS = RegExp(
    '^(s*[a-zA-Z0-9áéíóúAÁÉÍÓÚäëïöüÄËÏÖÜ\u00f1\u00d1]*)+(s*[a-zA-Z0-9áéíóúAÁÉÍÓÚäëïöüÄËÏÖÜ\u00f1\u00d1]*)*[a-zA-Z0-9áéíóúAÁÉÍÓÚäëïöüÄËÏÖÜ\u00f1\u00d1 ]*$'
  );

  const fetchRutStatus = () => {
    if (validateRut(enrollmentForm.getFieldValue('rut') || '')) {
      InitPage.rutStatus(enrollmentForm.getFieldValue('rut')?.replace(/\./g, '').replace(/-/g, ''))
        .then((response) => {
          setRutIsAlreadyRegistered(response.data.isAlreadyRegistered);
          setRutHasEnrollmentFormInProgress(response.data.hasEnrollmentFormInProgress);
          enrollmentForm.setFieldsValue({ name: response.data.legalPersonName });
        })
        .catch(() => message.error('Error inesperado. Por favor, intente nuevamente.'));
    }
  };

  const handleButtonNext = () => {
    const isMadeByThirdParty = enrollmentForm.getFieldValue('isMadeByThirdParty');

    if (!rutIsAlreadyRegistered && !rutHasEnrollmentFormInProgress) {
      dispatch({
        type: enrollmentAction.GO_TO_VIEW,
        payload: isMadeByThirdParty
          ? EnrollmentView.DeclaringPersonForm
          : EnrollmentView.LegalPersonForm,
      });
      history.push({
        pathname: '/enrolamiento/formulario/juridico',
      });
    }
  };

  const handleSubmit = () => {
    handleButtonNext();
  };

  useEffect(() => {
    if (rutIsAlreadyRegistered) {
      NotificationModal(
        <InfoCircleOutlined style={{ color: 'red' }} />,
        'RUT ya registrado',
        'El RUT ingresado ya se encuentra registrado en nuestro sistema.'
      );
    }
  }, [rutIsAlreadyRegistered]);

  useEffect(() => {
    if (rutHasEnrollmentFormInProgress) {
      NotificationModal(
        <InfoCircleOutlined style={{ color: 'red' }} />,
        'Solicitud en curso',
        'El RUT ingresado se encuentra con una solicitud en curso. Por favor presione el botón “Continuar con mi solicitud” para completar su proceso.'
      );
    }
  }, [rutHasEnrollmentFormInProgress]);

  useEffect(() => {
    const checked = enrollmentForm.getFieldValue(['legalPerson', 'hasAcceptedTerms']);
    if (checked) {
      setCheckedTerms(checked);
      enrollmentForm.setFieldsValue({
        legalPerson: { hasAcceptedTerms: checked },
      });
    }
  }, [enrollmentForm.getFieldValue(['legalPerson', 'hasAcceptedTerms'])]);

  const handleCheckboxChange = (e) => {
    setCheckedTerms(e.target.checked);
    enrollmentForm.setFieldsValue({ legalPerson: { hasAcceptedTerms: e.target.checked } }); // Actualiza el formulario
  };

  return (
    <Row className="initialLegalForm">
      <Col span={24}>
        <Row>
          <Col offset={4}>
            <p className="naturalForm__title">Persona Jurídica</p>
          </Col>
        </Row>
        <Form form={enrollmentForm} onFinish={handleSubmit} layout="vertical" requiredMark>
          <Row justify="center">
            <Col span={16}>
              <Form.Item
                name={['legalPerson', 'rut']}
                label="RUT empresa"
                rules={[
                  {
                    required: true,
                    validator: rutValidator,
                  },
                ]}
                validateTrigger="onBlur"
              >
                <InputRut type="text" maxLength={17} onBlur={fetchRutStatus} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="center">
            <Col span={16}>
              <Form.Item shouldUpdate noStyle>
                {() => (
                  <Form.Item
                    name={['legalPerson', 'name']}
                    label="Nombre o Razón Social"
                    rules={[
                      {
                        required: true,
                        message: 'Nombre es requerido',
                      },
                      {
                        pattern: allowLettersAndWS,
                        message: 'Ingrese solo letras y números',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row justify="center">
            <Col span={16}>
              <Form.Item
                name="isMadeByThirdParty"
                className="custom-label-text"
                label="¿El siguiente proceso de enrolamiento será
                realizado por un tercero en nombre de los representantes legales?"
                rules={[
                  {
                    required: true,
                    message: 'Dato requerido',
                  },
                ]}
              >
                <Radio.Group
                  options={[
                    { label: 'Si', value: true },
                    { label: 'No', value: false },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="center">
            <Col span={16}>
              <Form.Item
                name={['legalPerson', 'hasAcceptedTerms']}
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error('Terminos de aceptación es requerido')),
                  },
                ]}
              >
                <Row className="row-content-aceptTerm">
                  <Col>
                    <span className="required">*</span>
                  </Col>
                  <Col>
                    <Checkbox checked={checkedTerms} onChange={handleCheckboxChange}>
                      Acepto expresamente el uso y tratamiento de los datos proporcionados, para
                      fines comerciales, estadísticos e informativos propios de los
                      productos/servicios de Vantrust Capital y/o sus empresas relacionadas.
                    </Checkbox>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>
          <Row justify="center">
            <Col span={16}>
              <Form.Item>
                <Button htmlType="submit" className="initialLegalForm__button" type="primary">
                  {rutHasEnrollmentFormInProgress ? 'Continuar con mi solicitud' : 'Comenzar'}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

export default InitialLegalForm;
